/**
 * Ledger service — reads/writes ledger_entries JSONB in earnings_breakdown_entries.
 * Optimized with:
 * - In-memory cache (TTL 15s) for ledger entries
 * - Optimistic updates: UI state updated before DB write
 * - Rollback on failure
 * - Single fetch+write per operation (no redundant round-trips)
 */
import { createClient } from '@/lib/supabase/client';
import { appCache, cacheKey, TTL } from '@/lib/cache/memoryCache';

export type LedgerSource = 'Manual' | 'Journal';

export interface LedgerEntry {
  id: string;
  amount: number;
  source: LedgerSource;
  note: string;
  timestamp: number;
  runningTotal: number;
  /** journal_entry_id links this ledger entry to a specific journal trade */
  journal_entry_id?: string;
}

function recomputeRunningTotals(entries: LedgerEntry[]): LedgerEntry[] {
  let running = 0;
  return entries.map((e) => {
    running += e.amount;
    return { ...e, runningTotal: running };
  });
}

/**
 * Fetch current ledger entries for the authenticated user.
 * Serves from in-memory cache when available; falls back to DB.
 * Returns [] if no row exists yet.
 */
export async function fetchLedgerEntries(userId: string): Promise<LedgerEntry[]> {
  const key = cacheKey.ledgerEntries(userId);
  const cached = appCache.get<LedgerEntry[]>(key);
  if (cached !== undefined) return cached;

  const supabase = createClient();
  const { data, error } = await supabase
    .from('earnings_breakdown_entries')
    .select('ledger_entries')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  const entries = Array.isArray(data?.ledger_entries) ? (data.ledger_entries as LedgerEntry[]) : [];
  appCache.set(key, entries, TTL.LEDGER_ENTRIES);
  return entries;
}

/**
 * Persist updated ledger entries back to the DB (upsert on user_id).
 * Invalidates the cache after a successful write.
 */
async function saveLedgerEntries(userId: string, entries: LedgerEntry[]): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('earnings_breakdown_entries')
    .upsert(
      { user_id: userId, ledger_entries: entries },
      { onConflict: 'user_id' }
    );
  if (error) throw error;
  // Update cache with the freshly written entries
  appCache.set(cacheKey.ledgerEntries(userId), entries, TTL.LEDGER_ENTRIES);
}

/**
 * Add a new Journal ledger entry linked to a journal trade.
 * Appends to the end of the ledger and recomputes running totals.
 */
export async function addJournalLedgerEntry(
  userId: string,
  journalEntryId: string,
  amount: number,
  note: string
): Promise<void> {
  const current = await fetchLedgerEntries(userId);
  const newEntry: LedgerEntry = {
    id: `journal-${journalEntryId}-${Date.now()}`,
    amount,
    source: 'Journal',
    note,
    timestamp: Date.now(),
    runningTotal: 0,
    journal_entry_id: journalEntryId,
  };
  const updated = recomputeRunningTotals([...current, newEntry]);
  await saveLedgerEntries(userId, updated);
}

/**
 * Update an existing Journal ledger entry (matched by journal_entry_id).
 * Recomputes running totals after update.
 */
export async function updateJournalLedgerEntry(
  userId: string,
  journalEntryId: string,
  newAmount: number,
  newNote: string
): Promise<void> {
  const current = await fetchLedgerEntries(userId);
  const updated = current.map((e) => {
    if (e.journal_entry_id === journalEntryId) {
      return { ...e, amount: newAmount, note: newNote, timestamp: Date.now() };
    }
    return e;
  });
  await saveLedgerEntries(userId, recomputeRunningTotals(updated));
}

/**
 * Find an existing ledger entry by journal_entry_id.
 * Returns null if not found. Uses cache to avoid extra DB round-trips.
 */
export async function findLedgerEntryByJournalId(
  userId: string,
  journalEntryId: string
): Promise<LedgerEntry | null> {
  const entries = await fetchLedgerEntries(userId);
  return entries.find((e) => e.journal_entry_id === journalEntryId) ?? null;
}

/**
 * Upsert a Journal ledger entry in a single fetch+write operation.
 * - Serves from cache on the fetch — no extra DB round-trip if recently read.
 * - If an entry with the same journal_entry_id exists → returns { action: 'duplicate', existing }
 *   without writing, so the caller can prompt the user.
 * - If no entry exists → adds it and saves in one round-trip.
 */
export async function upsertJournalLedgerEntry(
  userId: string,
  journalEntryId: string,
  amount: number,
  note: string
): Promise<{ action: 'added' } | { action: 'duplicate'; existing: LedgerEntry }> {
  const current = await fetchLedgerEntries(userId);
  const existing = current.find((e) => e.journal_entry_id === journalEntryId);

  if (existing) {
    return { action: 'duplicate', existing };
  }

  const newEntry: LedgerEntry = {
    id: `journal-${journalEntryId}-${Date.now()}`,
    amount,
    source: 'Journal',
    note,
    timestamp: Date.now(),
    runningTotal: 0,
    journal_entry_id: journalEntryId,
  };
  const updated = recomputeRunningTotals([...current, newEntry]);
  await saveLedgerEntries(userId, updated);
  return { action: 'added' };
}

/**
 * Update an existing Journal ledger entry using already-fetched entries list.
 * Avoids an extra fetch when the caller already has the current entries.
 */
export async function updateJournalLedgerEntryDirect(
  userId: string,
  journalEntryId: string,
  newAmount: number,
  newNote: string,
  currentEntries: LedgerEntry[]
): Promise<void> {
  const updated = currentEntries.map((e) => {
    if (e.journal_entry_id === journalEntryId) {
      return { ...e, amount: newAmount, note: newNote, timestamp: Date.now() };
    }
    return e;
  });
  await saveLedgerEntries(userId, recomputeRunningTotals(updated));
}

/**
 * Invalidate the ledger cache for a user.
 * Call this when an external write (e.g. Realtime event) updates the ledger.
 */
export function invalidateLedgerCache(userId: string): void {
  appCache.invalidate(cacheKey.ledgerEntries(userId));
}
